import React, { FC } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import { DEFAULT_LOCALE, normalizeLocale } from '@rover/constants/src/i18n/locale';
import Section from '@rover/kibble/patterns/Section';
import { PetType } from '@rover/types/src/Pet';

import AppStoreCallout from '../../shared/AppStoreCallout/AppStoreCallout';
import LandingSearchResultsCarousel from '../components/LandingSearchResultsCarousel';
import { getSearchFiltersFromSeoLocationDetails } from '../utilities';
import useFacts from '../utilities/useFacts';

import QuickFact, { QuickFactHighlight } from './components/QuickFact';
import SeoHowBrandWorks from './components/SeoHowBrandWorks';
import SeoLocationFaqList from './components/SeoLocationFaqList';
import SeoLocationFooterLinks from './components/SeoLocationFooterLinks';
import SeoLocationHero from './components/SeoLocationHero';
import SeoLocationPageWrapper from './components/SeoLocationPageWrapper';
import SeoLocationReviewsCarousel from './components/SeoLocationReviewsCarousel';
import SeoLocationRoverProtect from './components/SeoLocationRoverProtect';
import SeoLocationText from './components/SeoLocationText';
import ValuePropFilterOptions from './components/ValuePropFilterOptions';
import WhyChooseBrand from './components/WhyChooseBrand';
import onValuePropFilterOptionsCtaClick from './utils/onValuePropFilterOptionsCtaClick';
import { quickFactBackgroundDoodle } from './images';
import { SeoLocationPageProps } from './types';

const SeoLocationPageTemplate: FC<SeoLocationPageProps> = ({
  seoLocation,
  countryConfig,
  frontendConfig,
  seoLocationDescriptor,
  reviews,
  serviceTypes,
  searchResponse,
}) => {
  const { linkedLocations, details, localityType } = seoLocation;
  const { language } = frontendConfig.settings;
  const locale = normalizeLocale(language) ?? DEFAULT_LOCALE;
  const initialSearchFilters = getSearchFiltersFromSeoLocationDetails(seoLocation);
  const {
    cityName,
    breadcrumbData,
    relatedServices,
    serviceTypeSlug,
    internalServiceType,
    petType,
    h2,
    facts,
    name: locationName,
  } = details;

  const {
    totalCustomersNum,
    medianListPricePerUnitLocalCurrencyNum,
    avgRepeatCustomerAmongSittersWithRepeatNum,
  } = useFacts(facts);
  // DEV-150772 update name to lowerCaseServiceTypeName. This was a temporary workaround for translations
  const serviceTypeName = details.serviceTypeName.toLocaleLowerCase(locale);

  // The price unit varies per service type and comes pre-localized from the backend
  // (e.g. "walk", "night", "visit", "day"), so we interpolate it into a single sentence.
  const renderMedianCostFact = (formattedAmount: string): JSX.Element => (
    <Trans>
      The median cost per {internalServiceType.alternativeRateUnitTranslated} for {serviceTypeName}{' '}
      in {locationName} is <QuickFactHighlight>{formattedAmount}</QuickFactHighlight>
    </Trans>
  );

  return (
    <SeoLocationPageWrapper seoLocation={seoLocation} countryConfig={countryConfig}>
      <Section>
        <SeoLocationHero
          subheading={h2}
          petType={petType}
          internalServiceTypeName={internalServiceType.name}
          serviceTypeName={details.serviceTypeName}
          locationName={locationName}
          locale={locale}
          serviceTypes={serviceTypes}
          serviceTypeSlug={serviceTypeSlug}
          initialSearchFilters={initialSearchFilters}
          showTrustpilot
        />
      </Section>

      {totalCustomersNum &&
        (() => {
          const { asNumber: ownerCount } = totalCustomersNum;
          return (
            <QuickFact stickerImageSrc={quickFactBackgroundDoodle}>
              <Trans>
                Over <QuickFactHighlight>{ownerCount}</QuickFactHighlight> pet parents in{' '}
                {locationName} have booked {serviceTypeName}
              </Trans>
            </QuickFact>
          );
        })()}

      <Section my={['16x', '12x']}>
        <LandingSearchResultsCarousel
          searchResponse={searchResponse}
          searchFilters={initialSearchFilters}
          locale={locale}
          heading={t`${locationName}, Find top rated ${serviceTypeName}`}
          headingVariant="heading"
          headingSize="400"
          serviceTypeSlug={serviceTypeSlug}
          countryConfig={countryConfig}
        />
      </Section>

      {medianListPricePerUnitLocalCurrencyNum &&
        (() => {
          const { asCurrency: formattedAmount } = medianListPricePerUnitLocalCurrencyNum;
          return (
            <QuickFact themeColor="pink" stickerImageSrc={quickFactBackgroundDoodle}>
              {renderMedianCostFact(formattedAmount)}
            </QuickFact>
          );
        })()}
      <SeoLocationReviewsCarousel
        seoLocation={seoLocation}
        reviews={reviews}
        searchFilters={initialSearchFilters}
        locale={locale}
        my={['16x', '12x']}
      />
      <SeoLocationRoverProtect />
      <WhyChooseBrand />
      <SeoHowBrandWorks
        serviceTypeName={serviceTypeName}
        providerNounPlural={internalServiceType.providerNounPlural}
        petType={(petType as PetType) ?? undefined}
      />
      <ValuePropFilterOptions
        my="12x"
        locationName={locationName}
        locale={locale}
        searchFilters={initialSearchFilters}
        internalServiceTypeSlug={internalServiceType.slug}
        petType={petType}
        serviceTypeName={serviceTypeName}
        onPress={onValuePropFilterOptionsCtaClick(serviceTypeSlug)}
      />

      {avgRepeatCustomerAmongSittersWithRepeatNum &&
        (() => {
          const { asNumber: clientCount } = avgRepeatCustomerAmongSittersWithRepeatNum;
          return (
            <QuickFact themeColor="cyan" stickerImageSrc={quickFactBackgroundDoodle}>
              <Trans>
                Sitters in {locationName} average{' '}
                <QuickFactHighlight>{clientCount}</QuickFactHighlight> repeat {serviceTypeName}{' '}
                clients
              </Trans>
            </QuickFact>
          );
        })()}
      <SeoLocationFaqList
        seoLocationDescriptor={seoLocationDescriptor}
        localityType={localityType}
        internalServiceTypeName={internalServiceType.name}
        locationName={locationName}
      />

      <SeoLocationText my="12x" text={details.text} />
      <Section mt={['8x', '16x']}>
        <AppStoreCallout />
      </Section>
      <SeoLocationFooterLinks
        breadcrumbData={breadcrumbData}
        serviceName={details.serviceTypeName}
        locationName={cityName}
        linkedLocations={linkedLocations}
        relatedServices={relatedServices}
        internalServiceTypeSlug={internalServiceType.slug}
      />
    </SeoLocationPageWrapper>
  );
};

export default SeoLocationPageTemplate;
